import React, { useEffect, useRef, useState } from "react";
import { AppShell } from "@/components/Nav";
import { useAuth } from "@/context/AuthContext";

function getToken() {
  return (
    localStorage.getItem("bm_auth_token") ||
    localStorage.getItem("bm_token") ||
    localStorage.getItem("token") ||
    ""
  );
}

export default function Concierge() {
  const { user, refreshUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [body,     setBody]     = useState("");
  const [loading,  setLoading]  = useState(true);
  const [sending,  setSending]  = useState(false);
  const [error,    setError]    = useState("");
  const [access,   setAccess]   = useState(true);
  const bottomRef = useRef(null);

  const fetchMessages = () => {
    const token = getToken();
    if (!token) { setAccess(false); setLoading(false); return; }
    fetch("/api/concierge/messages", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.status === 403) { setAccess(false); setLoading(false); return null; }
        return r.ok ? r.json() : Promise.reject(r.status);
      })
      .then((d) => { if (d) { setMessages(d); setLoading(false); } })
      .catch(() => { setError("Could not load messages."); setLoading(false); });
  };

  useEffect(() => {
    refreshUser().catch(() => {}).finally(() => fetchMessages());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = setInterval(fetchMessages, 5000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true); setError("");
    try {
      const token = getToken();
      const res = await fetch("/api/concierge/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ body: body.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Send failed");
      setBody("");
      fetchMessages();
    } catch (e) { setError(e.message); }
    finally { setSending(false); }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (!access && user?.role !== "admin" && user?.role !== "owner") {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">Private Table</div>
          <h2 className="font-display text-3xl mb-3">Concierge</h2>
          <p className="text-white/50 max-w-sm">
            Concierge support is available to Private Table members. Apply on the{" "}
            <a href="/membership" className="text-[#D4AF37] underline underline-offset-2">Membership</a> page.
          </p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex flex-col h-[calc(100vh-64px)] max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-4">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-1">Private Table</div>
          <h1 className="font-display text-2xl">BoraMoyo Concierge</h1>
          <p className="text-white/40 text-sm mt-1">Your dedicated matchmaking support line.</p>
        </div>

        {/* Message thread */}
        <div className="flex-1 overflow-y-auto space-y-3 py-2 pr-1">
          {loading && <div className="text-white/30 text-sm">Loading…</div>}
          {!loading && messages.length === 0 && (
            <div className="text-white/30 text-sm text-center pt-8">
              Send your first message — we respond within 24 hours.
            </div>
          )}
          {messages.map((m) => (
            <div
              key={m.message_id}
              className={`flex ${m.sender_role === "user" ? "justify-end" : "justify-start"}`}
            >
              {m.sender_role === "admin" && (
                <div className="h-7 w-7 rounded-full bg-[rgba(212,175,55,0.15)] flex items-center justify-center text-[10px] text-[#D4AF37] font-semibold mr-2 mt-1 flex-shrink-0">
                  BM
                </div>
              )}
              <div
                className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.sender_role === "user"
                    ? "bg-[rgba(212,175,55,0.15)] text-white rounded-br-sm"
                    : "glass text-white/90 rounded-bl-sm"
                }`}
              >
                {m.body}
                <div className="text-[10px] mt-1 opacity-40 text-right">
                  {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                </div>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="mt-3 pt-3 border-t border-white/8">
          {error && <p className="text-red-400 text-xs mb-2">{error}</p>}
          <div className="flex gap-2 items-end">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={handleKey}
              rows={2}
              placeholder="Message BoraMoyo Concierge…"
              className="flex-1 rounded-2xl bg-white/[0.04] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/25 outline-none focus:border-[rgba(212,175,55,0.4)] resize-none transition-colors"
            />
            <button
              onClick={send}
              disabled={sending || !body.trim()}
              className="h-11 w-11 flex-shrink-0 rounded-full bg-gradient-gold text-[#05070d] flex items-center justify-center disabled:opacity-40 transition-opacity"
              aria-label="Send"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" style={{ transform: "rotate(90deg)" }}>
                <path d="M2 21 23 12 2 3v7l15 2-15 2z" />
              </svg>
            </button>
          </div>
          <div className="text-[10px] text-white/20 mt-1.5 pl-1">Enter to send · Shift+Enter for new line</div>
        </div>
      </div>
    </AppShell>
  );
}
